// /support — list of tickets. Staff sees all; users see their own.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { SupportTicketSummary, TicketPriority, TicketStatus } from '../types';

const STATUS_BADGE: Record<TicketStatus, string> = {
  OPEN: 'badge-open',
  PENDING: 'badge-pending',
  WAITING_ON_CUSTOMER: 'badge-waiting',
  RESOLVED: 'badge-resolved',
  CLOSED: 'badge-closed',
};

const PRIORITY_BADGE: Record<TicketPriority, string> = {
  LOW: 'badge-prio-low',
  MEDIUM: 'badge-prio-medium',
  HIGH: 'badge-prio-high',
  URGENT: 'badge-prio-urgent',
};

const STATUS_LABEL: Record<TicketStatus, string> = {
  OPEN: 'Open',
  PENDING: 'Pending',
  WAITING_ON_CUSTOMER: 'Waiting on customer',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

export function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<SupportTicketSummary[]>('/api/support/tickets')
      .then(setTickets)
      .catch((err) => setError(err instanceof Error ? err.message : 'Load failed'));
  }, []);

  const isStaff = Boolean(user?.isStaff);

  return (
    <div className="page-content">
      <header className="dashboard-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>{isStaff ? 'All tickets' : 'Your tickets'}</h1>
          <p>{isStaff ? 'Helpdesk queue.' : 'Open a ticket to get help.'}</p>
        </div>
        <Link to="/support/new" className="btn-primary">New ticket</Link>
      </header>

      {error && <div className="form-error">{error}</div>}
      {!tickets && !error && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {tickets && tickets.length === 0 && (
        <p style={{ color: 'var(--text-muted)' }}>No tickets yet. Click "New ticket" to start.</p>
      )}

      {tickets && tickets.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Ticket #</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Priority</th>
              {isStaff && <th>Requester</th>}
              {isStaff && <th>Assignee</th>}
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id}>
                <td><Link to={`/support/${t.ticketNumber}`} className="btn-link" style={{ padding: 0 }}>{t.ticketNumber}</Link></td>
                <td>{t.subject}</td>
                <td><span className={`badge ${STATUS_BADGE[t.status]}`}>{STATUS_LABEL[t.status]}</span></td>
                <td><span className={`badge ${PRIORITY_BADGE[t.priority]}`}>{t.priority.toLowerCase()}</span></td>
                {isStaff && <td>{t.requester?.name ?? '—'}</td>}
                {isStaff && <td>{t.assignedTo?.name ?? <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>}</td>}
                <td>{new Date(t.lastReplyAt ?? t.updatedAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
